import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AttestationCycleRecordResponseRow } from '@risksmart-app/trpc/types/attestation-cycle.types';
import type { GetAttestationCyclesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC assessmentActivity data to match the GraphQL query structure
 */
const mapTrpcAttestationCyclesToGraphQL = (
  trpcData: AttestationCycleRecordResponseRow[]
): GetAttestationCyclesQuery => {
  return {
    attestation_cycle: trpcData.map((cycle) => {
      const document = cycle.document_file?.parent
        ? { ...cycle.document_file?.parent }
        : null;

      const documentFile = cycle.document_file
        ? { ...cycle.document_file, parent: document }
        : null;

      if (!documentFile) {
        throw new Error('Document file is missing in attestation cycle data');
      }

      return {
        ...cycle,
        records: cycle.attestation_record,
        parent: documentFile,
      };
    }),
  };
};

export const useGetAttestationCycles = (documentId: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.attestationCycle.byDocumentId.queryOptions({ documentId }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcAttestationCyclesToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
